import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * The swipe in progress. `mode` is decided once, from the first real movement of the finger:
   * 'right' = the drawer follows the finger, 'scroll' = an up/down drag that started outside the items
   * list (on the totals, buttons or header) and is scrolling the list for the shopper, 'ignore' =
   * anything else, left to the browser.
   * `blockClose` is true when a right swipe must not close the drawer (the touch started in a
   * sideways-scrolling row or a field being edited).
   * @type {{ startX: number, startY: number, startTime: number, dx: number, dy: number, mode: 'undecided' | 'right' | 'scroll' | 'ignore', blockClose: boolean, inList: boolean, lastY: number, lastTime: number, velocity: number } | null}
   */
  #swipe = null;

  /** @type {number | undefined} */
  #flingFrame;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Swipe right to close. All of these listeners are passive: the drawer follows the finger but
    // never cancels a gesture, so scrolling the items list, scrolling the upsell row and tapping
    // always behave natively. The listeners sit on the component itself, so they keep working when
    // the cart is re-rendered after an add or a quantity change.
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.#onTouchCancel, { passive: true });
  }

  /** The drawer's panel, looked up fresh each time so a re-render can't leave a stale reference. */
  #panel() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('touchmove', this.#onTouchMove);
    this.removeEventListener('touchend', this.#onTouchEnd);
    this.removeEventListener('touchcancel', this.#onTouchCancel);
  }

  /**
   * True when a touch started somewhere a right swipe means something else: a form field that is
   * being edited (an unfocused field, like the quantity number, is fine to swipe from), a slider,
   * or a row that scrolls sideways (like the upsell row) and still has content to the left to
   * scroll back to. A sideways row that is already at its left end can't move any further to the
   * right, so a right swipe there closes the drawer.
   * @param {EventTarget | null} target
   */
  #startsInSidewaysArea(target) {
    const dialog = this.#panel();
    let el = target instanceof Element ? target : null;
    while (el && el !== dialog) {
      if (el.matches('[role="slider"]')) return true;
      if (el.matches('input, textarea, select') && el === document.activeElement) return true;
      if (
        el.scrollLeft > 0 &&
        el.scrollWidth > el.clientWidth + 1 &&
        /(auto|scroll)/.test(getComputedStyle(el).overflowX)
      ) {
        return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  /** The scrolling list of cart items (the rest of the drawer, below it, is pinned). */
  #itemsList() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer__items'));
  }

  #stopFling() {
    if (this.#flingFrame) cancelAnimationFrame(this.#flingFrame);
    this.#flingFrame = undefined;
  }

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    this.#stopFling();
    const dialog = this.#panel();
    if (!dialog?.open || event.touches.length !== 1) {
      this.#swipe = null;
      return;
    }
    const touch = /** @type {Touch} */ (event.touches[0]);
    const items = this.#itemsList();
    this.#swipe = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      dx: 0,
      dy: 0,
      mode: 'undecided',
      blockClose: this.#startsInSidewaysArea(event.target),
      // A touch inside the items list scrolls natively; one anywhere else (totals, buttons, header)
      // would do nothing on its own, so its up/down movement is passed on to the list below.
      inList: !!items && items.contains(/** @type {Node} */ (event.target)),
      lastY: touch.clientY,
      lastTime: Date.now(),
      velocity: 0,
    };
  };

  /** @param {TouchEvent} event */
  #onTouchMove = (event) => {
    const swipe = this.#swipe;
    const dialog = this.#panel();
    if (!swipe || swipe.mode === 'ignore' || !dialog) return;

    const touch = /** @type {Touch} */ (event.touches[0]);
    const dx = touch.clientX - swipe.startX;
    const dy = touch.clientY - swipe.startY;

    if (swipe.mode === 'undecided') {
      // Wait for a real movement, then decide once. A pull to the right that is at least as wide as
      // it is tall moves the drawer (unless this touch started somewhere a right swipe means
      // something else); an up/down drag that started outside the items list scrolls the list;
      // everything else is left to the browser.
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (dx > 0 && dx >= Math.abs(dy) && !swipe.blockClose) {
        swipe.mode = 'right';
        dialog.style.transition = 'none';
      } else if (Math.abs(dy) > Math.abs(dx) && !swipe.inList) {
        swipe.mode = 'scroll';
      } else {
        swipe.mode = 'ignore';
        return;
      }
    }

    if (swipe.mode === 'scroll') {
      const items = this.#itemsList();
      const now = Date.now();
      const step = touch.clientY - swipe.lastY;
      if (items) items.scrollTop -= step;
      const dt = Math.max(1, now - swipe.lastTime);
      // Smoothed speed of the finger (px per ms), used for the flick at the end
      swipe.velocity = swipe.velocity * 0.6 + (step / dt) * 0.4;
      swipe.lastY = touch.clientY;
      swipe.lastTime = now;
      return;
    }

    swipe.dx = Math.max(0, dx);
    swipe.dy = dy;
    // `translate` is separate from `transform`, so it stacks on top of the drawer's slide animation
    dialog.style.translate = `${swipe.dx}px 0`;
  };

  #onTouchEnd = () => {
    const swipe = this.#swipe;
    this.#swipe = null;
    const dialog = this.#panel();

    // A drag that was scrolling the list from outside it ends with a short, decaying flick
    if (swipe?.mode === 'scroll') {
      const items = this.#itemsList();
      let velocity = Date.now() - swipe.lastTime < 80 ? swipe.velocity : 0;
      if (!items || Math.abs(velocity) < 0.15) return;
      const glide = () => {
        const before = items.scrollTop;
        items.scrollTop -= velocity * 16;
        velocity *= 0.94;
        this.#flingFrame =
          Math.abs(velocity) > 0.03 && items.scrollTop !== before ? requestAnimationFrame(glide) : undefined;
      };
      this.#flingFrame = requestAnimationFrame(glide);
      return;
    }

    if (swipe?.mode !== 'right' || !dialog) return;

    const width = dialog.getBoundingClientRect().width || 1;
    const elapsed = Math.max(1, Date.now() - swipe.startTime);
    // The whole gesture has to have been mostly sideways, not a vertical scroll that began diagonally
    const mostlySideways = Math.abs(swipe.dy) <= swipe.dx * 1.5;
    const farEnough = swipe.dx > Math.max(80, width * 0.3);
    const flicked = swipe.dx > 40 && swipe.dx / elapsed > 0.5;

    if (mostlySideways && (farEnough || flicked)) {
      dialog.style.transition = '';
      this.closeDialog().then(() => {
        dialog.style.translate = '';
      });
    } else {
      // Not far enough, or not really sideways: spring back
      dialog.style.transition = 'translate 0.2s ease';
      dialog.style.translate = '0px 0';
      setTimeout(() => {
        dialog.style.transition = '';
        dialog.style.translate = '';
      }, 220);
    }
  };

  #onTouchCancel = () => {
    const swipe = this.#swipe;
    this.#swipe = null;
    this.#stopFling();
    const dialog = this.#panel();
    if (swipe?.mode === 'right' && dialog) {
      dialog.style.transition = '';
      dialog.style.translate = '';
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
